/* ══════════════════════════════════════════════════════════════════════════
   Squishit — shared store layer.
   Owns: the catalogue, the cart (localStorage), the pricing rules, and the
   cart drawer, which is injected once here rather than repeated per page.

   PORTING TO SHOPIFY
   ------------------
   Everything below the PRICING block is presentation and can carry over.
   Replace the model itself:
     · CATALOGUE      → products/variants from Shopify
     · readCart/write → /cart.js + /cart/add.js + /cart/change.js (AJAX API)
     · colour         → a variant option (or a line-item property)
     · SET_DISCOUNT   → an automatic discount / Shopify Function, NOT client maths
     · shipping       → shipping profile with a free-shipping-over-₹1,499 rate
   The 5% online-payment incentive is a checkout-level discount, not a cart line.
   ══════════════════════════════════════════════════════════════════════════ */
(function (window, document) {
  'use strict';

  /* ─────────────────────────────  catalogue  ─────────────────────────────
     The real first drop: 19 SKUs, classified from the delivered photography
     (folder numbers = photo dirs in img/products/). Names are working titles
     for Shlok to confirm. Ratings/counts are layout placeholders like the
     review figures. photos = shippable frames after dedupe, hero first. */
  var CATALOGUE = {};
  var ALL = [];

  var CATS = { food: 'Food', shapes: 'Shapes', creatures: 'Creatures' };

  /* A second browse axis. Every value is observable in the product photo —
     spikes, sugar grain, moulded beads, glitter, speckles. We do NOT publish
     hardness or firmness: the client hasn't supplied durometer data and
     inventing it would be a spec claim. */
  var FEELS = { smooth: 'Smooth', textured: 'Textured', glitter: 'Glitter' };
  var FEEL_OF = {
    doughnut: 'smooth', puffer: 'textured', 'soft-serve': 'smooth', slice: 'smooth',
    cloud: 'textured', bear: 'smooth', 'ice-pop': 'smooth', 'lemon-drop': 'textured',
    'cake-slice': 'smooth', marble: 'smooth', 'ice-cube': 'smooth', pyramid: 'textured',
    'ember-egg': 'smooth', smiley: 'textured', bunny: 'glitter', jellyfish: 'glitter',
    heart: 'glitter', orb: 'textured', bao: 'smooth'
  };

  [
    ['doughnut',   '1',  'The Doughnut',   'food',      'Blue icing, sprinkles, and a squeeze that never runs out.',
      'A ring of dense gel under blue icing and sprinkles. Squeeze it flat and watch it rise back like it was never touched.', 14, 'Bestseller', '4.8', 212],
    ['puffer',     '2',  'The Puffer',     'shapes',    'A field of soft tendrils that spring back every time.',
      'Hundreds of soft tendrils in wavy ridges. Rake it, press it, squash it, it springs back to a perfect round.', 9, '', '4.7', 164],
    ['soft-serve', '3',  'The Soft Serve', 'food',      'A two tone swirl that never melts on the job.',
      'A two tone swirl on a waffle cone, cast in dense gel. The swirl folds under your grip and unwinds slowly.', 9, '', '4.8', 97],
    ['slice',      '4',  'The Slice',      'food',      'Cheese, crust and pepperoni, minus the grease.',
      'Cheese, crust and three proud pepperoni. Fold it like a real slice, then watch it flatten back out.', 4, '', '4.7', 83],
    ['cloud',      '5',  'The Cloud',      'shapes',    'A puff of bubbles that squashes flat and floats back.',
      'A puff of pink bubbles stacked into a cloud. Every bubble dents on its own before the whole thing bounces back.', 11, '', '4.8', 76],
    ['bear',       '6',  'The Bear',       'creatures', 'The gummy classic, grown to a proper handful.',
      'The gummy classic at desk scale. Dense, translucent and far too satisfying to leave alone.', 10, '', '4.6', 71],
    ['ice-pop',    '7',  'The Ice Pop',    'food',      'Mid melt forever, never sticky.',
      'Frozen mid melt with a drip that never falls. The stick is soft too, so the whole thing squashes.', 9, '', '4.7', 64],
    ['lemon-drop', '8',  'The Lemon Drop', 'food',      'Sugar crystal texture with a deep round crush.',
      'A sugar textured sphere with a deep, round crush. The grain catches the light while it flexes.', 3, '', '4.6', 58],
    ['cake-slice', '9',  'The Cake Slice', 'food',      'Three pastel layers, one very slow bite back.',
      'Three pastel layers under white icing. Press the wedge and the layers bulge like real sponge.', 3, '', '4.7', 52],
    ['marble',     '10', 'The Marble',     'shapes',    'A marbled swirl that reads different from every angle.',
      'No two angles look the same. A marbled swirl runs through the whole body, corner to corner.', 15, 'New', '4.8', 47],
    ['ice-cube',   '11', 'The Ice Cube',   'shapes',    'Ice cold look, dense warm squeeze.',
      'Looks straight out of the freezer, squeezes warm and dense. The clear body glows at the edges.', 15, '', '4.7', 44],
    ['pyramid',    '12', 'The Pyramid',    'shapes',    'Four corners, one point, endless presses.',
      'Four corners, one point, endless presses. The grain texture makes every dent visible.', 12, '', '4.6', 39],
    ['ember-egg',  '13', 'The Ember Egg',  'shapes',    'An amber core in a crown of red.',
      'An amber core in a crown of red. Thick at the middle, glowing at the tips.', 12, '', '4.7', 36],
    ['smiley',     '14', 'The Smiley',     'creatures', 'A beaded grin that rolls under your fingers.',
      'A beaded ball with a moulded grin. The texture rolls under your fingers while the face holds its smile.', 12, '', '4.6', 33],
    ['bunny',      '15', 'The Bunny',      'creatures', 'A glitter jelly rabbit that always sits back up.',
      'A glitter jelly rabbit that slumps when squeezed and sits back up when you let go.', 9, '', '4.8', 31],
    ['jellyfish',  '16', 'The Jellyfish',  'creatures', 'Glitter bell, wavy tentacles, zero sting.',
      'A glitter dome with wavy tentacles. Squash the bell and the tentacles sway.', 9, 'Most gifted', '4.8', 29],
    ['heart',      '17', 'The Heart',      'shapes',    'Chunky glitter and sequins in a glossy shell.',
      'A glossy shell packed with chunky glitter and sequins. Heavy in the hand, slow on the return.', 9, '', '4.9', 88],
    ['orb',        '18', 'The Orb',        'shapes',    'Speckled, smooth, and impossibly round.',
      'Speckled like an egg, round as anything. A smooth even crush with no corners to find.', 12, '', '4.6', 24],
    ['bao',        '19', 'The Bao',        'food',      'A steamed bun with a face. Squeeze gently.',
      'A steamed bun with a face. Pleats on top, dense gel underneath, and a slow rise back after every press.', 4, 'New', '4.7', 21]
  ].forEach(function (row, i) {
    var id = row[0];
    ALL.push(id);
    CATALOGUE[id] = {
      id: id, folder: row[1], name: row[2], cat: row[3],
      no: ('00' + (i + 1)).slice(-3),
      benefit: row[4], desc: row[5],
      photos: row[6], badge: row[7], rating: row[8], count: row[9],
      pop: 20 - (i + 1), fresh: i + 1,
      feel: FEEL_OF[id] || 'smooth',
      /* The bar used to cycle red/ink/grey by index, which put the accent on
         cards that had earned nothing and made red decorative. Red is reserved
         for a card that actually carries a badge. */
      /* keyed to the COLUMN, not the index: with a two-row rail, i % 2 puts
         every even card in row 1 and every odd in row 2, so the whole second
         row wore the pale cap and read as de-emphasised */
      bar: row[7] ? 'var(--color-accent)' : ['var(--color-neutral-800)', 'var(--color-neutral-400)'][Math.floor(i / 2) % 2]
    };
  });

  /* ─────────────────────────  where things come from  ─────────────────────
     Static build: relative paths and localStorage. Shopify: flat CDN-hashed
     assets (snippets/sq-data.liquid hands us the prefix), real product images
     and prices off the products themselves, and real URLs. One codebase, both
     grounds. */
  var BRIDGE = (typeof window !== 'undefined' && window.SQ_DATA) || null;
  var SHOP = {};
  if (BRIDGE && BRIDGE.products) {
    BRIDGE.products.forEach(function (p) { SHOP[p.handle] = p; });
  }

  function asset(name) {
    return BRIDGE && BRIDGE.assetBase ? BRIDGE.assetBase + name : null;
  }

  /* photo url helper: n is 1-based, 1 = hero packshot */
  function imgSrc(x) { return (x && typeof x === 'object') ? x.src : x; }

  function photo(id, n) {
    var live = SHOP[id];
    if (live && live.images && live.images.length) {
      return imgSrc(live.images[Math.min((n || 1) - 1, live.images.length - 1)]);
    }
    if (BRIDGE) return asset('sq-p' + CATALOGUE[id].folder + '-' + (n || 1) + '.jpg');
    return 'img/products/' + CATALOGUE[id].folder + '/' + (n || 1) + '.jpg';
  }

  function photoCount(id) {
    var live = SHOP[id];
    if (live && live.images && live.images.length) return live.images.length;
    return CATALOGUE[id].photos;
  }

  /* transparent cutout of the product, for floating/inline decoration */
  function cutout(id) {
    return asset('sq-cut-' + id + '.png') || 'img/cutouts/' + id + '.png';
  }

  /* standalone decorative art (hero field, gel macro, shapes crop) */
  function art(name) { return asset('sq-' + name) || 'img/' + name; }

  /* every product link goes through here */
  function urlOf(id) {
    var live = SHOP[id];
    if (live && live.url) return live.url;
    if (BRIDGE) return '/products/' + id;
    return 'product.html?shape=' + id;   /* static build */
  }
  /* "index.html#x" is not a Shopify route — it serves the 404 template. Resolve
     homepage anchors through the bridge instead, never a hardcoded '/', which
     would break under market/locale subfolders. */
  function homeUrl(hash) {
    var root = (BRIDGE && BRIDGE.routes && BRIDGE.routes.root) || 'index.html';
    return root.replace(/\/$/, '/') + hash;
  }

  function collectionUrl(qs) {
    var base = (BRIDGE && BRIDGE.routes && BRIDGE.routes.collection) || 'collection.html';
    return qs ? base + '?' + qs : base;
  }

  /* ─────────────────────────  lifestyle frames  ──────────────────────────
     One filled, edge-to-edge frame per SKU: a real hand holding or crushing
     the product on a saturated colour field. Picked by reading every frame in
     all 19 folders — these are the ones where the subject reaches the edges
     and the squeeze is legible, rather than a small object floating in dead
     air. Used wherever a band needs imagery instead of a cutout on a grid. */
  var LIFE_FRAME = {
    doughnut: 13, puffer: 9, 'soft-serve': 9, slice: 4, cloud: 5, bear: 9,
    'ice-pop': 8, 'lemon-drop': 2, 'cake-slice': 2, marble: 10, 'ice-cube': 8,
    pyramid: 2, 'ember-egg': 2, smiley: 11, bunny: 8, jellyfish: 8, heart: 8,
    orb: 4, bao: 4
  };
  function life(id) {
    /* The lifestyle frames were HAND-PICKED by folder index (reading all 193
       frames). Live product images override photo() BY INDEX, so one wrong
       upload order would silently swap every tile. Decorative surfaces
       therefore always use the theme-asset frame, never the live gallery. */
    var n = LIFE_FRAME[id] || 2;
    return asset('sq-p' + CATALOGUE[id].folder + '-' + n + '.jpg')
        || 'img/products/' + CATALOGUE[id].folder + '/' + n + '.jpg';
  }

  /* ────────────────────────  the delivery date  ──────────────────────────
     Conversion framework, tier 1: a DATE beats a duration everywhere, and 41%
     of sites still print a speed. Same +4 working-ish day window the pincode
     check uses, so the two can never disagree. Replace with the courier's real
     serviceability response on Shopify. */
  function deliveryDate(days) {
    var d = new Date();
    d.setDate(d.getDate() + (days || 4));
    return d.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });
  }

  /* ───────────────────────  colour chips  ────────────────────────────────
     Sampled from each colourway's own hero photograph — the product pixels,
     not the backdrop — then name-corrected where a transparent product read
     its own background ("Clear" sampled pink). Two stops per colourway so a
     two-tone shape like "Blue Icing On Pink" chips honestly instead of
     pretending to be one flat colour. */
  var COLOUR_HEX = {
    'bao': { 'Pink': ['#f7c3dd', '#dd8fa9'] },
    'bear': { 'Lilac Purple': ['#c38fdd', '#ddc3f7'], 'Ice Blue': ['#a9dddd', '#5bc3dd'], 'Bubblegum Pink': ['#f7c3dd', '#f775a9'] },
    'bunny': { 'Neon Yellow': ['#ddf78f', '#c3dd41'], 'Glitter Purple': ['#a975c3', '#ddc3dd'], 'Bubblegum Pink': ['#f7c3dd', '#f78fa9'] },
    'cake-slice': { 'White With Pastel Stripes': ['#f2f2f0', '#75c3c3'] },
    'cloud': { 'Hot Pink': ['#f78fc3', '#f7dddd'], 'Ocean Blue': ['#c3f7f7', '#5ba9dd'], 'Golden Yellow': ['#f7c375', '#f7a90d'], 'Orchid Purple': ['#a975c3', '#5b4175'] },
    'doughnut': { 'Blue Icing On Pink': ['#8fc3f7', '#ddc3dd'], 'Chocolate With White Drizzle': ['#dda98f', '#a9755b'], 'Purple On Yellow': ['#ddc3f7', '#a975dd'], 'Pink On Peach': ['#f7c3c3', '#c35b41'] },
    'ember-egg': { 'Ember Red': ['#dd755b', '#c32727'], 'Emerald Green': ['#0d4127', '#0d8f5b'], 'Amber Orange': ['#f7a95b', '#dd7541'], 'Sapphire Blue': ['#415ba9', '#c3ddf7'] },
    'heart': { 'Pink': ['#f78fa9', '#f7c3dd'], 'Purple': ['#c38fdd', '#7541a9'], 'Mint Aqua': ['#a9dddd', '#8fa9c3'] },
    'ice-cube': { 'Blue': ['#41c3dd', '#75ddf7'], 'Clear': ['#dfe8ee', '#dd758f'], 'Purple': ['#a941c3', '#f7c3f7'], 'Frosted Rose': ['#dd8f8f', '#f7c3c3'], 'Frosted Lilac': ['#c375a9', '#dda9c3'] },
    'ice-pop': { 'Pink & Mint': ['#f7a9c3', '#dd75a9'], 'Pink & Frosted Clear': ['#f78fc3', '#ddc375'], 'Orange & Magenta': ['#f7a98f', '#f75ba9'] },
    'jellyfish': { 'Baby Pink': ['#f7c3dd', '#c38fa9'], 'Orchid Pink': ['#f7c3f7', '#c375c3'], 'Ice Blue': ['#c3ddf7', '#8fa9dd'] },
    'lemon-drop': { 'Lemon Yellow': ['#f7dda9', '#f7dd0d'] },
    'marble': { 'Pink & Blue Swirl': ['#5b41dd', '#a975f7'], 'Lilac Marble': ['#5b2775', '#8f418f'], 'Blue & Violet Marble': ['#a9a9f7', '#8f41c3'], 'Green Marble': ['#0d8f5b', '#758fa9'], 'Amber Orange': ['#dd5b27', '#f7755b'] },
    'orb': { 'White With Blue Speckles': ['#c3ddf7', '#8fc3dd'], 'Blue': ['#0d5ba9', '#0d8fdd'], 'Magenta Pink': ['#dd278f', '#f75bc3'], 'Lime Yellow': ['#c3dd0d', '#dddd5b'] },
    'puffer': { 'Neon Green': ['#8fc327', '#c3dddd'], 'Orange': ['#f7dda9', '#dd8f41'], 'Bubblegum Pink': ['#f78fc3', '#dd5b8f'] },
    'pyramid': { 'Hot Pink': ['#f775a9', '#f7a9dd'], 'Purple': ['#a95bc3', '#dd8fdd'], 'Ice Blue': ['#75a9dd', '#8fddf7'], 'Amber Yellow': ['#f7dda9', '#f7c35b'] },
    'slice': { 'Coral Crust & Pink Pepperoni': ['#f7ddc3', '#f7755b'] },
    'smiley': { 'Lavender Purple': ['#c38fdd', '#ddc3f7'], 'Coral Pink': ['#f7a9c3', '#dd415b'], 'Aqua Blue': ['#8fdddd', '#c3f7f7'], 'Orange': ['#f7a95b', '#f7750d'] },
    'soft-serve': { 'Orange And White Swirl': ['#f7ddc3', '#f78f41'], 'Pink And White Swirl': ['#f775a9', '#f7c3dd'], 'Mint And White Swirl': ['#a9dddd', '#ddc375'] }
  };
  function colourHex(id, name) {
    var m = COLOUR_HEX[id];
    return (m && m[name]) || null;
  }

  /* ─────────────────────────  colourways  ────────────────────────────────
     A product's images all live on the product; the FRONT END decides which
     ones to show. Attribution is belt and braces: the CSV wrote alt text as
     "<Title> — <Colour> — <shot>", and our filenames are
     <handle>-<colour-slug>-<n>.jpg which Shopify preserves in the CDN URL.
     Alt is tried first, filename second, so either surviving is enough. */
  function colourSlug(s) {
    return String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  }

  /* every colour this product actually sells, in variant order */
  function coloursOf(id) {
    var live = SHOP[id];
    if (!live || !live.variants) return [];
    var seen = {}, out = [];
    live.variants.forEach(function (v) {
      var c = v.option1;
      if (!c || c === 'Default Title' || seen[c]) return;
      seen[c] = 1;
      out.push({ name: c, slug: colourSlug(c), variantId: v.id,
                 available: v.available, price: v.price, image: v.image,
                 hex: colourHex(id, c) });
    });
    return out;
  }

  /* the images belonging to one colour, in product order */
  function imagesFor(id, colour) {
    var live = SHOP[id];
    if (!live || !live.images) return [];
    var all = live.images;
    if (!colour) return all.map(imgSrc);
    var slug = colourSlug(colour), lc = String(colour).toLowerCase();
    /* Exact colour first: alt is "<Title> — <Colour>[ — shot]" and files are
       <handle>-<colour-slug>-<n>. A plain substring test lets "Blue" also
       claim "White With Blue Speckles", so try the exact segment before it. */
    var exact = all.filter(function (im) {
      var alt = (im && im.alt ? im.alt : '').toLowerCase();
      var parts = alt.split('—').map(function (s) { return s.trim(); });
      if (parts.indexOf(lc) !== -1) return true;
      var file = imgSrc(im).split('/').pop().split('?')[0].toLowerCase();
      return file.indexOf(id + '-' + slug + '-') === 0;
    });
    if (exact.length) return exact.map(imgSrc);
    var hit = all.filter(function (im) {
      var alt = (im && im.alt ? im.alt : '').toLowerCase();
      if (alt.indexOf(lc) !== -1) return true;
      var file = imgSrc(im).split('/').pop().split('?')[0].toLowerCase();
      return file.indexOf(slug) !== -1;
    });
    /* never strand the customer on an empty gallery */
    return (hit.length ? hit : all).map(imgSrc);
  }

  function variantFor(id, colour) {
    var live = SHOP[id];
    if (!live || !live.variants) return null;
    for (var i = 0; i < live.variants.length; i++) {
      if (live.variants[i].option1 === colour) return live.variants[i];
    }
    return live.variants[0] || null;
  }

  /* ───────────────────────  PRICING — locked by the handoff  ───────────── */
  var UNIT = 799;          // per squishy
  var MRP = 999;           // strikethrough anchor
  var SET_DISCOUNT = 198;  // flat, when all three shapes are in the cart
  var SHIP_FLAT = 200;
  var SHIP_FREE_OVER = 1499;
  var MAX_QTY = 9;

  var fmt = function (n) { return '₹' + n.toLocaleString('en-IN'); };

  function priceOf(id) {
    var live = SHOP[id];
    return live && live.price ? live.price : UNIT;
  }
  function isLive(id) { return !!SHOP[id]; }

  /* Single source of truth for every money number on the site.
     With 19 shapes, "the set" generalizes: ANY three distinct shapes in the
     cart earn the flat set discount. Same economics as the original trio
     (3 x 799 - 198 = 2,199, ships free), now catalogue-size independent. */
  var SET_SIZE = 3;
  function totals(cart) {
    var have = function (id) { return cart.some(function (i) { return i.id === id; }); };
    var distinct = cart.filter(function (i) { return i.qty > 0; }).length;
    var setDone = distinct >= SET_SIZE;
    var count = cart.reduce(function (n, i) { return n + i.qty; }, 0);
    var subtotal = cart.reduce(function (n, i) { return n + UNIT * i.qty; }, 0);
    var disc = setDone ? SET_DISCOUNT : 0;
    var sub = subtotal - disc;
    var ship = (sub >= SHIP_FREE_OVER || sub === 0) ? 0 : SHIP_FLAT;
    return {
      have: have, setDone: setDone, distinct: Math.min(distinct, SET_SIZE),
      needed: Math.max(0, SET_SIZE - distinct), count: count,
      subtotal: subtotal, disc: disc, sub: sub, ship: ship,
      total: sub + ship,
      pct: Math.min(100, Math.round(sub / SHIP_FREE_OVER * 100))
    };
  }

  /* top-popularity shapes not yet in the cart, for one-click set completion */
  function suggestMissing(cart, n) {
    var have = function (id) { return cart.some(function (i) { return i.id === id; }); };
    return ALL.slice().sort(function (a, b) {
      return CATALOGUE[b].pop - CATALOGUE[a].pop;
    }).filter(function (id) { return !have(id); }).slice(0, n);
  }

  /* ───────────────────────────  cart storage  ──────────────────────────── */
  var KEY = 'sq-cart';
  var listeners = [];

  function readCart() {
    try {
      var c = JSON.parse(localStorage.getItem(KEY) || '[]');
      return Array.isArray(c) ? c.filter(function (i) {
        return i && CATALOGUE[i.id] && i.qty > 0;
      }) : [];
    } catch (e) { return []; }
  }

  function writeCart(c) {
    syncShopifyCart(c);
    try { localStorage.setItem(KEY, JSON.stringify(c)); } catch (e) {}
    emit();
  }

  function emit() {
    var cart = readCart();
    listeners.forEach(function (fn) { fn(cart); });
  }

  function onChange(fn) { listeners.push(fn); fn(readCart()); }

  /* Flash "Added ✓" on the button that was actually pressed, so the click
     is acknowledged before the drawer animation steals attention. */
  function confirmButton(btn) {
    if (!btn || btn.dataset.sqBusy) return;
    var label = btn.innerHTML;
    btn.dataset.sqBusy = '1';
    btn.classList.add('is-added');
    btn.innerHTML = 'Added ✓';
    setTimeout(function () {
      btn.innerHTML = label;
      btn.classList.remove('is-added');
      delete btn.dataset.sqBusy;
    }, 1100);
  }

  /* Upsert by id, capped at MAX_QTY. `open` defaults to true.
     (Colour variants dropped: each of the 19 shapes ships one colourway,
     as photographed. On Shopify this becomes a variant again if needed.) */
  /* ─────────────────────────  the cart boundary  ──────────────────────────
     THE RULE OF THIS PORT: Shopify owns money. The localStorage cart is the
     drawer's DISPLAY model and the demo path while products are not yet
     imported. The moment a product exists (it has a variantId from the
     bridge), every add is mirrored into the REAL Shopify cart via the AJAX
     API, and checkout charges what Shopify says — never what this file says.
     The set discount (any 3 distinct − ₹198) must be recreated in admin as an
     automatic discount; the drawer's maths is a preview of that rule, not the
     source of it. */
  var syncTimer = null;
  function syncShopifyCart(cart) {
    if (!BRIDGE) return;
    /* absolute quantities for every live variant; 0 removes. Debounced so a
       burst of stepper clicks lands as one call, always with the final state. */
    clearTimeout(syncTimer);
    syncTimer = setTimeout(function () {
      var updates = {};
      ALL.forEach(function (id) {
        var live = SHOP[id];
        if (!live) return;
        if (live.variants) live.variants.forEach(function (v) { updates[v.id] = 0; });
        else if (live.variantId) updates[live.variantId] = 0;
      });
      cart.forEach(function (line) {
        var live = SHOP[line.id];
        var vid = line.vid || (live && live.variantId);
        if (vid) updates[vid] = (updates[vid] || 0) + line.qty;
      });
      if (!Object.keys(updates).length) return;
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates: updates })
      }).catch(function () { /* next write re-sends the full state */ });
    }, 250);
  }

  function addItem(id, qty, open, variantId) {
    if (!CATALOGUE[id]) return;
    var c = readCart().map(function (i) { return { id: i.id, qty: i.qty, vid: i.vid }; });
    /* a colour is its own line: same shape in two colours must reach checkout
       as two variants, not one doubled quantity */
    var vid = variantId || (SHOP[id] && SHOP[id].variantId) || null;
    var k = c.filter(function (i) { return i.id === id && (i.vid || null) === vid; })[0];
    if (k) k.qty = Math.min(MAX_QTY, k.qty + (qty || 1));
    else c.push({ id: id, qty: qty || 1, vid: vid });
    writeCart(c);
    if (open !== false) openDrawer();
  }

  function setQty(idx, qty) {
    var c = readCart().map(function (i) { return { id: i.id, qty: i.qty, vid: i.vid }; });
    if (!c[idx]) return;
    c[idx].qty = Math.min(MAX_QTY, qty);
    writeCart(c.filter(function (i) { return i.qty > 0; }));
  }

  function removeAt(idx) {
    writeCart(readCart().filter(function (x, j) { return j !== idx; }));
  }

  var WA_ICON = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><path d="M21 12a8 8 0 0 1-11.6 7.2L4 21l1.8-5.4A8 8 0 1 1 21 12z"/></svg>';

  /* ─────────────────────────────  drawer  ──────────────────────────────── */
  var root = null, coNote = false;

  function drawerMarkup() {
    return '' +
      '<div class="scrim" data-close></div>' +
      '<aside class="drawer" role="dialog" aria-modal="true" aria-label="Your cart">' +
        '<div class="dr-head">' +
          '<strong>Your cart · <span data-dr-count>0</span></strong>' +
          '<button class="dr-x" data-close aria-label="Close cart">×</button>' +
        '</div>' +
        '<div class="dr-ship">' +
          '<div class="dr-ship-row"><span data-ship-msg></span><span class="dr-ship-pct" data-ship-pct></span></div>' +
          '<div class="meter"><div class="meter-fill" data-ship-fill style="width:0%"></div></div>' +
        '</div>' +
        '<div data-dr-content></div>' +
      '</aside>';
  }

  function linesMarkup(cart, t) {
    var html = '<div class="dr-body">';
    cart.forEach(function (i, idx) {
      var p = CATALOGUE[i.id];
      html += '<div class="dr-line">' +
        '<img class="dr-thumb" src="' + photo(i.id, 1) + '" alt="' + p.name + '" width="44" height="44" loading="lazy">' +
        '<div class="dr-line-main">' +
          '<div class="dr-line-top"><strong>' + p.name + '</strong><strong>' + fmt(UNIT * i.qty) + '</strong></div>' +
          '<span class="dr-line-meta">' + p.benefit + '</span>' +
          '<div class="dr-line-ctl">' +
            '<div class="stepper">' +
              '<button data-dec="' + idx + '" aria-label="Decrease quantity">−</button>' +
              '<span>' + i.qty + '</span>' +
              '<button data-inc="' + idx + '" aria-label="Increase quantity">+</button>' +
            '</div>' +
            '<button class="dr-rm" data-rm="' + idx + '">Remove</button>' +
          '</div>' +
        '</div>' +
      '</div>';
    });

    /* set progress: any three distinct shapes = the set price */
    html += '<div class="dr-set">' +
      '<div class="dr-set-row"><strong>Set progress</strong>' +
      '<span class="dr-set-count">' + t.distinct + ' / 3 SHAPES</span></div>' +
      '<div class="dr-set-glyphs">';
    for (var gi = 0; gi < 3; gi++) {
      html += '<span class="dr-set-sq' + (gi < t.distinct ? ' is-on' : '') + '"></span>';
    }
    html += '</div>';
    if (t.count > 0 && !t.setDone) {
      var sugg = suggestMissing(cart, t.needed);
      var names = sugg.map(function (id) { return CATALOGUE[id].name.replace('The ', 'the '); }).join(' + ');
      html += '<button class="kc kc--light kc--md" data-add-missing>Add ' + names + ' — complete the set</button>';
    }
    if (t.setDone) {
      html += '<span class="dr-set-done">Set complete — ₹198 off applied · collector cards in the box</span>';
    }
    html += '</div></div>';

    /* totals */
    html += '<div class="dr-foot">';
    if (t.setDone) html += '<div class="dr-tot-row"><span>Set discount</span><b>−' + fmt(SET_DISCOUNT) + '</b></div>';
    html += '<div class="dr-tot-row"><span>Shipping</span><span>' +
      (t.sub === 0 ? '—' : t.ship === 0 ? 'FREE' : fmt(SHIP_FLAT)) + '</span></div>' +
      '<div class="dr-tot"><strong>Total</strong><strong>' + fmt(t.total) + '</strong></div>' +
      '<span class="dr-fine">Pay online and an extra 5% comes off at checkout. COD available.</span>' +
      '<button class="kc kc--accent kc-block" data-checkout>Checkout · ' + fmt(t.total) + '</button>' +
      (coNote ? '<span class="dr-note">Prototype — checkout wires to Shopify at build.</span>' : '') +
      '<div class="dr-pay"><span class="tag tag-outline">UPI</span><span class="tag tag-outline">Cards</span>' +
      '<span class="tag tag-outline">Netbanking</span><span class="tag tag-outline">COD</span></div>' +
      '</div>';
    return html;
  }

  function emptyMarkup() {
    var html = '<div class="dr-empty">' +
      '<div><h3>Nothing squished yet.</h3>' +
      '<span class="dr-empty-sub">Start with a bestseller — every shape ships with its collector card.</span></div>';
    suggestMissing([], 3).forEach(function (id) {
      var p = CATALOGUE[id];
      html += '<div class="dr-sug">' +
        '<img class="dr-thumb" src="' + photo(id, 1) + '" alt="' + p.name + '" width="38" height="38" loading="lazy">' +
        '<div class="dr-sug-main"><strong>' + p.name + '</strong><span>' + fmt(UNIT) + ' · ★ ' + p.rating + '</span></div>' +
        '<button class="kc kc--light kc--sm" data-sug="' + id + '">Add</button>' +
      '</div>';
    });
    html += '<div class="dr-wa">' + WA_ICON + 'Stuck? WhatsApp us — a real person replies.</div></div>';
    return html;
  }

  function renderDrawer() {
    if (!root) return;
    var cart = readCart(), t = totals(cart);

    root.querySelector('[data-dr-count]').textContent = t.count;
    root.querySelector('[data-ship-msg]').textContent =
      t.sub === 0 ? 'Free shipping over ' + fmt(SHIP_FREE_OVER)
        : t.sub >= SHIP_FREE_OVER ? 'Free shipping unlocked — you saved ' + fmt(SHIP_FLAT)
          : fmt(SHIP_FREE_OVER - t.sub) + ' away from free shipping';
    root.querySelector('[data-ship-pct]').textContent = t.pct + '%';
    root.querySelector('[data-ship-fill]').style.width = t.pct + '%';
    root.querySelector('[data-dr-content]').innerHTML = t.count > 0 ? linesMarkup(cart, t) : emptyMarkup();
  }

  function openDrawer() {
    coNote = false;
    if (!root) return;
    root.hidden = false;
    document.body.style.overflow = 'hidden';
    renderDrawer();
  }

  function closeDrawer() {
    coNote = false;
    if (!root) return;
    root.hidden = true;
    document.body.style.overflow = '';
  }

  function mountDrawer() {
    root = document.getElementById('drawerRoot');
    if (!root) return;
    root.className = 'drawer-root';
    root.hidden = true;
    root.innerHTML = drawerMarkup();

    root.addEventListener('click', function (e) {
      var el = e.target.closest('[data-close],[data-inc],[data-dec],[data-rm],[data-add-missing],[data-checkout],[data-sug]');
      if (!el) return;
      if (el.hasAttribute('data-close')) return closeDrawer();

      var cart = readCart();
      if (el.hasAttribute('data-inc')) return setQty(+el.getAttribute('data-inc'), cart[+el.getAttribute('data-inc')].qty + 1);
      if (el.hasAttribute('data-dec')) return setQty(+el.getAttribute('data-dec'), cart[+el.getAttribute('data-dec')].qty - 1);
      if (el.hasAttribute('data-rm')) return removeAt(+el.getAttribute('data-rm'));
      if (el.hasAttribute('data-sug')) {
        return addItem(el.getAttribute('data-sug'), 1, false);
      }
      if (el.hasAttribute('data-add-missing')) {
        return suggestMissing(cart, totals(cart).needed).forEach(function (id) {
          addItem(id, 1, false);
        });
      }
      if (el.hasAttribute('data-checkout')) {
        /* Live store: hand over to the REAL checkout — Shopify's cart already
           mirrors this one via shopifyAdd. Static build: keep the demo note. */
        if (BRIDGE) { location.href = '/checkout'; return; }
        coNote = true; return renderDrawer();
      }
    });
  }

  /* ───────────────────────────  header wiring  ─────────────────────────── */
  function mountHeader() {
    var btn = document.querySelector('[data-open-cart]');
    if (btn) btn.addEventListener('click', openDrawer);
  }

  /* ─────────────────────────────  WhatsApp  ────────────────────────────
     TODO(client): Squishit's real WhatsApp number is still outstanding.
     Fill it here — every chip on every page reads from this one constant.
     Format: country code + number, digits only, no '+'.                   */
  var WA_NUMBER = '';   /* e.g. '919880065190' */

  function mountWa() {
    var pills = document.querySelectorAll('[data-wa]');
    pills.forEach(function (el) {
      el.addEventListener('click', function () {
        if (WA_NUMBER) window.open('https://wa.me/' + WA_NUMBER, '_blank', 'noopener');
        else location.href = homeUrl('#faq');   /* until the number lands */
      });
    });
    if (!pills.length) return;

    /* The pill is fixed bottom-right and was sitting on top of the last cell
       of the facts strip at first paint. Hold it back until the first screen
       is behind you. This gates a floating control, never page content — the
       no-scroll-reveal rule is about content appearing, and nothing here is. */
    var show = function () {
      var on = window.scrollY > 260;
      pills.forEach(function (el) { el.classList.toggle('is-on', on); });
    };
    window.addEventListener('scroll', show, { passive: true });
    window.addEventListener('resize', show);
    show();
  }

  var lastCount = null;
  function syncCounts(cart) {
    var t = totals(cart);
    document.querySelectorAll('[data-cart-count]').forEach(function (el) {
      var changed = lastCount !== null && String(t.count) !== el.textContent;
      el.textContent = t.count;
      /* An empty cart shouldn't be the loudest thing in the header — a red
         badge reading "0" out-shouted the one button that sells. */
      el.hidden = t.count === 0;
      if (!changed) return;
      /* restart the keyframe: remove, force reflow, re-add */
      el.classList.remove('is-bump');
      void el.offsetWidth;
      el.classList.add('is-bump');
    });
    lastCount = t.count;
    if (root && !root.hidden) renderDrawer();
  }

  /* ─────────────────────────  accordion (shared)  ──────────────────────── */
  function mountAccordions() {
    document.querySelectorAll('[data-acc]').forEach(function (group) {
      group.addEventListener('click', function (e) {
        var btn = e.target.closest('.acc-btn');
        if (!btn) return;
        var item = btn.parentElement;
        var open = item.classList.contains('is-open');
        /* single-open */
        group.querySelectorAll('.acc-item').forEach(function (i) {
          i.classList.remove('is-open');
          i.querySelector('.acc-btn').setAttribute('aria-expanded', 'false');
          i.querySelector('.acc-btn i').textContent = '+';
        });
        if (!open) {
          item.classList.add('is-open');
          btn.setAttribute('aria-expanded', 'true');
          btn.querySelector('i').textContent = '−';
        }
      });
    });
  }

  /* ─────────────────────────────  boot  ────────────────────────────────── */
  /* One delegated listener for every "add" button on any page, so the
     confirm animation and the add stay in lockstep and no page has to
     re-implement either. */
  function mountAddButtons() {
    document.addEventListener('click', function (e) {
      var b = e.target.closest('[data-add]');
      if (!b || b.closest('#drawerRoot')) return;
      var id = b.getAttribute('data-add');
      if (!CATALOGUE[id]) return;
      confirmButton(b);
      addItem(id, 1, true);
    });
  }


  /* ══════════════════════════════ THE SUPERMENU ══════════════════════════════
     Structure decoded from the Flow Ninja screen recording (5 Aug), the same
     reference the Calibr mega menu was built from:
       · the panel hangs off the nav and the nav gives up its own bottom edge,
         so nav + panel read as ONE surface rather than a box under a box
       · panel one is a grid of visual TILE CARDS, staggered fade+rise, left to
         right, ~50ms apart — not a text list
       · panel two is DELIBERATELY a different layout: labelled link columns
         plus a promo card. The two panels are not meant to match.
       · opens on hover with a close grace, so travelling into it never shuts it

     Built here, once, rather than in four HTML files — same discipline as the
     Calibr universal nav. Every page picks it up, and it reads the catalogue,
     so a twentieth shape appears in the menu with no menu edit.
     Static-first: the panel's markup is complete and clickable before any
     motion class lands, so it survives JS-off styling and reduced motion.
     ═════════════════════════════════════════════════════════════════════════ */
  var MEGA_CLOSE_GRACE = 220;   // ms of travel time between nav and panel

  function megaShopPanel() {
    var axes = [
      { key: 'cat',  map: CATS,  lead: { food: 'ice-pop', shapes: 'marble', creatures: 'bear' } },
      { key: 'feel', map: FEELS, lead: { smooth: 'ice-cube', textured: 'lemon-drop', glitter: 'heart' } }
    ];
    var i = 0;
    var tiles = axes.map(function (ax) {
      return '<div class="mega-axis">' +
        '<span class="mega-kick">By ' + (ax.key === 'cat' ? 'shape' : 'feel') + '</span>' +
        '<div class="mega-tiles">' +
          Object.keys(ax.map).map(function (k) {
            var ids = ALL.filter(function (id) { return CATALOGUE[id][ax.key] === k; });
            var hero = ax.lead[k] || ids[0];
            return '<a class="mega-tile" style="--i:' + (i++) + '" href="' + collectionUrl(ax.key + '=' + k) + '">' +
              '<img src="' + life(hero) + '" alt="" loading="lazy">' +
              '<span class="mega-tile-veil"></span>' +
              '<span class="mega-tile-body"><strong>' + ax.map[k] + '</strong>' +
              '<em>' + ids.length + '</em></span>' +
            '</a>';
          }).join('') +
        '</div>' +
      '</div>';
    }).join('');
    return '<div class="mega-shop">' + tiles + '</div>';
  }

  function megaCollectionPanel() {
    var byPop = ALL.slice().sort(function (a, b) { return CATALOGUE[b].pop - CATALOGUE[a].pop; });
    var col = function (title, items) {
      return '<div class="mega-col"><span class="mega-kick">' + title + '</span>' +
        items.map(function (it) {
          return '<a href="' + it[1] + '">' + it[0] +
            (it[2] ? '<i>' + it[2] + '</i>' : '') + '</a>';
        }).join('') + '</div>';
    };
    var top = byPop.slice(0, 6).map(function (id) {
      return [CATALOGUE[id].name, urlOf(id), 'No. ' + CATALOGUE[id].no];
    });
    var newest = ALL.slice().sort(function (a, b) { return CATALOGUE[b].fresh - CATALOGUE[a].fresh; })
      .slice(0, 4).map(function (id) {
        return [CATALOGUE[id].name, urlOf(id), CATALOGUE[id].badge || ''];
      });

    return '<div class="mega-cols">' +
      col('Most squished', top) +
      col('Newest in', newest) +
      col('Browse', [
        ['All ' + ALL.length + ' shapes', collectionUrl(), ''],
        ['Under one roof', collectionUrl(), ''],
        ['The next shape', homeUrl('#join'), 'No. 020']
      ]) +
      /* Was a packshot on a pastel gradient sitting beside six photographs of
         real hands — two visual languages in one menu, and the rendered one is
         the one that reads fake. Same lifestyle frame family as the tiles, and
         the same scrim recipe, which measures 4.9-9.1:1 where the bare plate
         measured 2.11:1. */
      '<a class="mega-promo" href=' + homeUrl('#set') + '>' +
        '<img src="' + life('marble') + '" alt="" loading="lazy">' +
        '<span class="mega-tile-veil"></span>' +
        '<span class="mega-promo-body">' +
          '<span class="mega-kick">Any three</span>' +
          '<strong>Build your set</strong>' +
          '<em>Three shapes, one box, ' + fmt(2199) + '</em>' +
          '<span class="mega-promo-cta">Pick three &#8594;</span>' +
        '</span>' +
      '</a>' +
    '</div>';
  }

  var PANELS = { shop: megaShopPanel, collection: megaCollectionPanel };

  function mountMega() {
    var nav = document.querySelector('.hdr-nav');
    var wrap = document.querySelector('.hdr-wrap');
    if (!nav || !wrap) return;

    /* Swap the two catalogue links for buttons that own a panel. Everything
       else in the nav is left exactly as it is. */
    var host = document.createElement('div');
    host.className = 'mega';
    host.hidden = true;
    wrap.appendChild(host);

    var open = null, closeTimer = null;

    function paint(which) {
      if (!PANELS[which]) return;
      host.innerHTML = '<div class="mega-inner" data-panel="' + which + '">' + PANELS[which]() + '</div>';
    }
    function show(which) {
      clearTimeout(closeTimer);
      if (open === which) return;
      open = which;
      paint(which);
      host.hidden = false;
      document.body.classList.add('mega-on');
      nav.querySelectorAll('[data-mega]').forEach(function (b) {
        b.setAttribute('aria-expanded', String(b.getAttribute('data-mega') === which));
      });
      requestAnimationFrame(function () { host.classList.add('is-in'); });
    }
    function hide(now) {
      clearTimeout(closeTimer);
      closeTimer = setTimeout(function () {
        open = null;
        host.classList.remove('is-in');
        document.body.classList.remove('mega-on');
        nav.querySelectorAll('[data-mega]').forEach(function (b) { b.setAttribute('aria-expanded', 'false'); });
        setTimeout(function () { if (!open) host.hidden = true; }, 200);
      }, now ? 0 : MEGA_CLOSE_GRACE);
    }

    nav.querySelectorAll('[data-mega]').forEach(function (b) {
      var which = b.getAttribute('data-mega');
      b.addEventListener('mouseenter', function () { show(which); });
      b.addEventListener('focus', function () { show(which); });
      b.addEventListener('click', function (e) {
        e.preventDefault();
        if (open === which) hide(true); else show(which);
      });
    });
    nav.addEventListener('mouseleave', function () { hide(); });
    host.addEventListener('mouseenter', function () { clearTimeout(closeTimer); });
    host.addEventListener('mouseleave', function () { hide(); });
    host.addEventListener('click', function (e) { if (e.target.closest('a')) hide(true); });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape') hide(true); });
  }

  function init() {
    mountDrawer();
    mountAddButtons();
    mountHeader();
    mountMega();
    mountAccordions();
    mountWa();
    onChange(syncCounts);

    /* keep other open tabs in sync */
    window.addEventListener('storage', function (e) { if (e.key === KEY) emit(); });
    window.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') { closeDrawer(); if (window.SQ.onEsc) window.SQ.onEsc(); }
    });

    /* ?cart=1 deep-links the drawer open */
    if (/(^|[?&])cart=1/.test(location.search)) openDrawer();
  }

  window.SQ = {
    CATALOGUE: CATALOGUE, ALL: ALL, CATS: CATS, FEELS: FEELS,
    UNIT: UNIT, MRP: MRP, SET_DISCOUNT: SET_DISCOUNT,
    SHIP_FLAT: SHIP_FLAT, SHIP_FREE_OVER: SHIP_FREE_OVER,
    fmt: fmt, totals: totals, photo: photo, photoCount: photoCount,
    cutout: cutout, life: life, art: art, asset: asset,
    coloursOf: coloursOf, imagesFor: imagesFor, variantFor: variantFor, colourSlug: colourSlug,
    colourHex: colourHex,
    urlOf: urlOf, collectionUrl: collectionUrl, homeUrl: homeUrl,
    priceOf: priceOf, isLive: isLive, SHOP: SHOP, BRIDGE: BRIDGE,
    deliveryDate: deliveryDate, suggestMissing: suggestMissing,
    confirmButton: confirmButton,
    readCart: readCart, addItem: addItem, onChange: onChange,
    openDrawer: openDrawer, closeDrawer: closeDrawer,
    init: init
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

})(window, document);
